import { useEffect, useState } from 'react';
import axios from 'axios';
import './PrivateLessons.css';
import {
  API_BASE,
  authHeaders,
  formatDateTime,
  statusBadgeClass,
  statusLabel
} from './privateLessonUtils.js';

function AllPrivateLessons({ embedded = false, title = 'Privelessen' }) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [actionId, setActionId] = useState('');

  const fetchLessons = async () => {
    try {
      setIsLoading(true);

      const res = await axios.get(`${API_BASE}/private-lesson/getall`, {
        params: {
          page: 1,
          limit: 100,
          search,
          status: statusFilter
        },
        headers: authHeaders()
      });

      setItems(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      console.error('Error fetching private lessons', err);
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLessons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const postAction = async (id, action, confirmText) => {
    const confirmed = window.confirm(confirmText);
    if (!confirmed) return;

    try {
      setActionId(`${action}-${id}`);
      await axios.post(
        `${API_BASE}/private-lesson/${id}/${action}`,
        {},
        { headers: authHeaders() }
      );
      fetchLessons();
    } catch (err) {
      console.error(`Error ${action} private lesson`, err);
      alert(err?.response?.data?.error || 'Actie mislukt.');
    } finally {
      setActionId('');
    }
  };

  const cancelLesson = async (id) => {
    const reason = window.prompt('Reden van annuleren? Je mag dit leeg laten.');
    if (reason === null) return;

    try {
      setActionId(`cancel-${id}`);
      await axios.post(
        `${API_BASE}/private-lesson/${id}/cancel`,
        { reason: reason || null },
        { headers: authHeaders() }
      );
      fetchLessons();
    } catch (err) {
      console.error('Error cancelling private lesson', err);
      alert(err?.response?.data?.error || 'Annuleren mislukt.');
    } finally {
      setActionId('');
    }
  };

  const deleteLesson = async (id) => {
    const confirmed = window.confirm('Weet je zeker dat je deze priveles wilt verwijderen?');
    if (!confirmed) return;

    try {
      setActionId(`delete-${id}`);
      await axios.delete(`${API_BASE}/private-lesson/${id}`, {
        headers: authHeaders()
      });
      fetchLessons();
    } catch (err) {
      console.error('Error deleting private lesson', err);
      alert(err?.response?.data?.error || 'Verwijderen mislukt.');
    } finally {
      setActionId('');
    }
  };

  return (
    <div className={embedded ? 'private-lessons-embedded' : 'bg-white p-4'}>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        {embedded ? (
          <h4 className="mb-0 private-home-title">{title}</h4>
        ) : (
          <h2 className="mb-0">{title}</h2>
        )}
        <span className="text-muted">{items.length} les(sen)</span>
      </div>

      <hr className="seperator" />

      <div className="row mb-3">
        <div className="col-md-6 mb-2">
          <input
            type="text"
            className="form-control rounded-0"
            placeholder="Zoek op leerling, docent of onderwerp"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') fetchLessons();
            }}
          />
        </div>

        <div className="col-md-3 mb-2">
          <select
            className="form-select rounded-0"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Alle statussen</option>
            <option value="requested">Aangevraagd</option>
            <option value="confirmed">Akkoord</option>
            <option value="cancelled">Geannuleerd</option>
            <option value="completed">Afgerond</option>
            <option value="no_show">No-show</option>
          </select>
        </div>

        <div className="col-md-3 mb-2">
          <button
            type="button"
            className="btn btn-outline-secondary w-100 rounded-0"
            onClick={fetchLessons}
          >
            Zoeken
          </button>
        </div>
      </div>

      {isLoading ? (
        <p>Loading...</p>
      ) : items.length === 0 ? (
        <div className="alert alert-light border">Geen privelessen gevonden.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered align-middle">
            <thead>
              <tr>
                <th>Leerling</th>
                <th>Ouder</th>
                <th>Docent</th>
                <th>Onderwerp</th>
                <th>Datum/tijd</th>
                <th>Duur</th>
                <th>Status</th>
                <th style={{ minWidth: 260 }}>Acties</th>
              </tr>
            </thead>

            <tbody>
              {items.map((item) => {
                const status = String(item.status || '').toLowerCase();

                return (
                  <tr key={item.private_lesson_id}>
                    <td>{item.student_name || '-'}</td>
                    <td>{item.parent_name || '-'}</td>
                    <td>{item.docent_name || '-'}</td>
                    <td>{item.subject || 'Priveles'}</td>
                    <td>{formatDateTime(item.start_at)}</td>
                    <td>{item.duration_minutes || '-'} min</td>
                    <td>
                      <span className={statusBadgeClass(status)}>
                        {statusLabel(status)}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex gap-2 flex-wrap">
                        {status === 'requested' && (
                          <button
                            type="button"
                            className="btn btn-success btn-sm"
                            disabled={actionId === `confirm-${item.private_lesson_id}`}
                            onClick={() =>
                              postAction(
                                item.private_lesson_id,
                                'confirm',
                                'Deze priveles bevestigen?'
                              )
                            }
                          >
                            Akkoord
                          </button>
                        )}

                        {['requested', 'confirmed', 'planned'].includes(status) && (
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            disabled={actionId === `cancel-${item.private_lesson_id}`}
                            onClick={() => cancelLesson(item.private_lesson_id)}
                          >
                            Annuleren
                          </button>
                        )}

                        {['confirmed', 'planned'].includes(status) && (
                          <>
                            <button
                              type="button"
                              className="btn btn-outline-success btn-sm"
                              disabled={actionId === `complete-${item.private_lesson_id}`}
                              onClick={() =>
                                postAction(
                                  item.private_lesson_id,
                                  'complete',
                                  'Deze priveles afronden?'
                                )
                              }
                            >
                              Afgerond
                            </button>

                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              disabled={actionId === `no-show-${item.private_lesson_id}`}
                              onClick={() =>
                                postAction(
                                  item.private_lesson_id,
                                  'no-show',
                                  'Deze priveles als no-show markeren?'
                                )
                              }
                            >
                              No-show
                            </button>
                          </>
                        )}

                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          disabled={actionId === `delete-${item.private_lesson_id}`}
                          onClick={() => deleteLesson(item.private_lesson_id)}
                        >
                          Verwijderen
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AllPrivateLessons;
